import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import { formatMonth } from ".";
import { formatMoney, formatMoneyCompact } from "@/lib/format";

interface RevenueChartProps {
  data: Array<{
    month: string;
    current: number;
    previousYear: number;
  }>;
}

export default function RevenueChart({ data }: RevenueChartProps) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        {t("system.analytics.revenue.overTime")}
      </h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorPrevious" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#9ca3af" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonth}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tickFormatter={(v: number) => formatMoneyCompact(v)}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
              // Recharts osu nerozšiřuje podle obsahu popisků. Od převodu na koruny je
              // popisek „1,2 mil. Kč" (11 znaků ≈ 65 px při fontSize 12), ne „1.2M“ —
              // v původních 60 px se ořezával. Grafy s holým číslem (TrafficChart)
              // si 60 px drží.
              width={84}
            />
            <Tooltip
              formatter={(value: number) => [formatMoney(value), ""]}
              labelFormatter={formatMonth}
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            />
            <Legend
              formatter={(value) =>
                value === "current"
                  ? t("system.analytics.revenue.thisYear")
                  : t("system.analytics.revenue.lastYear")
              }
            />
            <Area
              type="monotone"
              dataKey="previousYear"
              name="previousYear"
              stroke="#9ca3af"
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="url(#colorPrevious)"
            />
            <Area
              type="monotone"
              dataKey="current"
              name="current"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#colorCurrent)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
